// Single source of truth for turning a raw chat-history-archive row into the
// UI `Answer` shape that `AnswerItem` renders. Extracted from `chat.tsx` so the
// main chat AND the analytics/feedback view parse archived turns identically
// (DRY) — same output-text handling, same sources, same scopes/attachments.

import { Source, ScopesObject, parseChatAttachments } from '@/shared/types';
import { toProvenanceLookupPath } from '@/shared/functions';
import {
  uiKeysForEvidenceListingTokens,
  fileTypesTokensForEvidenceListing,
} from '@/components/evidence-locker-ui-shared-new';
import {
  encodePreliminaryMessages,
  encodePreliminaryMessageStrings,
  PROCESSING_MESSAGE,
} from '@/components/preliminary-messages';
import type { Answer } from '@/components/answer-item';
import type { TranslateFn } from '@/i18n/useTimePilotT';

export interface AnswerItemFromHistory {
  question: string;
  answer: string;
  session_id: string;
  time_saved: number;
  scopes: string[];
  files: string[];
  excluded_scopes: string[];
  excluded_files: string[];
  epoch_time_secs: number;
  citations?: Source[];
  content_categories?: string[];
  excluded_content_categories?: string[];
  evidence_category_chips?: string[];
  evidence_category_scopes?: string[];
  excluded_evidence_category_scopes?: string[];
  evidence_all_file_types?: boolean;
  /**
   * Server-assigned identity used (with `epoch_time_secs`) to memoise
   * the per-item fetch for the lifetime of the browser session. See
   * `shared/chat-history-cache.ts` for the caching policy.
   */
  chat_key?: string;
  /**
   * Lifecycle label echoed by the assistant service. Items in any
   * non-terminal state (`"inprogress"`, `"initializing"`) bypass the
   * session cache so the UI keeps re-fetching while the backend is
   * still producing output.
   */
  status?: string;
  request_type?: string;
}

export const RETRY_MESSAGE =
  'TimePilot was unable to answer this question at this time. Please refresh the page and try again. If the issue persists, please provide feedback using the button in the lower-right.';

export function isTimelineHistoryItem(item: Pick<AnswerItemFromHistory, 'request_type'>): boolean {
  return item.request_type === 'timeline' || item.request_type === 'timeline_build';
}

export function answerTextFromHistoryRow(
  item: AnswerItemFromHistory,
  answer: Record<string, unknown> | null,
  t?: TranslateFn,
): string {
  const initializingMessage = t?.('chat.progress.initializing') ?? 'Initializing...';
  const processingMessage = t?.('chat.progress.processing') ?? PROCESSING_MESSAGE;
  const retryMessage = t?.('chat.errors.retryAnswer') ?? RETRY_MESSAGE;
  const status = typeof item.status === 'string' ? item.status : 'unknown';
  const isInitializing = status === 'initializing';
  const isInProgress = status === 'inprogress';

  if (!answer) {
    if (isInitializing) {
      return encodePreliminaryMessageStrings([initializingMessage]);
    }
    if (isInProgress) {
      return encodePreliminaryMessageStrings([processingMessage]);
    }
    return retryMessage;
  }

  const outputStr = typeof answer.output === 'string' ? answer.output : '';
  const outputIsEmpty = outputStr.trim() === '';
  const isPreliminary =
    'is_preliminary' in answer && answer.is_preliminary === true;
  const containsMessages =
    Array.isArray(answer.messages) && answer.messages.length > 0;
  const containsPreliminaryResults = 'messages' in answer;
  // A `completed` status row is always final regardless of what the stored JSON
  // says — is_preliminary may still be true in archived rows that finished
  // streaming, which would otherwise make isPreliminaryResult win and show
  // "Analyzing documents..." instead of the real output.
  const isFinalResult =
    status === 'completed' ||
    (containsPreliminaryResults &&
      'is_preliminary' in answer &&
      answer.is_preliminary === false) ||
    (!containsPreliminaryResults && !(isInitializing || isInProgress || isPreliminary));
  const isPreliminaryResult = !isFinalResult && (isInitializing || isInProgress || isPreliminary);

  if (isInitializing) {
    return encodePreliminaryMessageStrings([initializingMessage]);
  }
  if (isPreliminaryResult && containsMessages) {
    return encodePreliminaryMessages(
      answer.messages as Array<{ content?: string }>,
    );
  }
  if (isPreliminaryResult && !outputIsEmpty) {
    return encodePreliminaryMessageStrings([outputStr]);
  }
  if (isPreliminaryResult && outputIsEmpty) {
    return encodePreliminaryMessageStrings([processingMessage]);
  }
  if (!isFinalResult) {
    const rawMessages = Array.isArray(answer.messages)
      ? (answer.messages as Array<{ content?: string }>)
      : [];
    return encodePreliminaryMessages(rawMessages);
  }
  if (!outputIsEmpty) {
    return outputStr;
  }
  return retryMessage;
}

export function mapHistoryItemToAnswer(item: AnswerItemFromHistory, t?: TranslateFn): Answer | null {
  const status = typeof item.status === 'string' ? item.status : 'unknown';
  const isNonFinalStatus = status === 'initializing' || status === 'inprogress';

  let answer: Record<string, unknown> | null = null;
  if (typeof item.answer === 'string' && item.answer.trim() !== '') {
    try {
      answer = JSON.parse(item.answer) as Record<string, unknown>;
    } catch {
      if (!isNonFinalStatus) {
        return null;
      }
    }
  } else if (!isNonFinalStatus) {
    return null;
  }

  const answerText = answerTextFromHistoryRow(item, answer, t);
  const contentCategoriesUi =
    item.content_categories != null
      ? uiKeysForEvidenceListingTokens(item.content_categories)
      : null;
  const evidenceCategoryChipsUi =
    item.evidence_category_chips != null
      ? uiKeysForEvidenceListingTokens(item.evidence_category_chips)
      : null;
  const evidenceCategoryScopesUi =
    item.evidence_category_scopes != null
      ? uiKeysForEvidenceListingTokens(item.evidence_category_scopes)
      : null;
  const excludedContentCategoriesWire =
    item.excluded_content_categories != null
      ? item.excluded_content_categories
      : null;
  const excludedEvidenceCategoryScopesUi =
    item.excluded_evidence_category_scopes != null
      ? uiKeysForEvidenceListingTokens(item.excluded_evidence_category_scopes)
      : excludedContentCategoriesWire != null && excludedContentCategoriesWire.length > 0
        ? uiKeysForEvidenceListingTokens(excludedContentCategoriesWire)
        : null;
  const hasIncludedCategoryScopes =
    (evidenceCategoryScopesUi?.length ?? contentCategoriesUi?.length ?? 0) > 0;
  const hasExcludedCategoryScopes = (excludedEvidenceCategoryScopesUi?.length ?? 0) > 0;
  const evidenceAllFileTypesFromHistory =
    hasIncludedCategoryScopes || hasExcludedCategoryScopes
      ? false
      : item.evidence_all_file_types != null
        ? !!item.evidence_all_file_types
        : undefined;

  return {
    question: item.question,
    answerText,
    sources: (answer?.citations as Source[]) || [],
    epochTimeSecs: item.epoch_time_secs,
    sessionId: item.session_id,
    timeSaved: item.time_saved,
    resultForFeedback: item,
    // Attachments live as a sibling field on the row (next to `status`,
    // `epoch_time_secs`, …), not inside the JSON `answer`. Carry them onto the
    // Answer so AnswerItem can render chips directly from this already-loaded
    // row instead of relying on its secondary per-row fetch — which is skipped
    // entirely for rows that are already `completed` on load (TR-1453).
    attachments: parseChatAttachments((item as { attachments?: unknown }).attachments),
    scopesObject: {
      scopes: item.scopes || [],
      files: (item.files || []).map(toProvenanceLookupPath),
      excludedScopes: item.excluded_scopes || [],
      excludedFiles: (item.excluded_files || []).map(toProvenanceLookupPath),
      ...(contentCategoriesUi != null
        ? { contentCategories: contentCategoriesUi }
        : {}),
      ...(evidenceCategoryChipsUi != null
        ? { evidenceCategoryChips: evidenceCategoryChipsUi }
        : contentCategoriesUi != null && evidenceCategoryScopesUi == null
          ? { evidenceCategoryChips: contentCategoriesUi }
          : {}),
      ...(evidenceCategoryScopesUi != null
        ? { evidenceCategoryScopes: evidenceCategoryScopesUi }
        : contentCategoriesUi != null
          ? { evidenceCategoryScopes: contentCategoriesUi }
          : {}),
      ...(evidenceAllFileTypesFromHistory !== undefined
        ? { evidenceAllFileTypes: evidenceAllFileTypesFromHistory }
        : {}),
      // Include empty arrays when the server field is present so merge can
      // distinguish "explicitly no exclusions" from "field omitted".
      ...(item.excluded_content_categories != null
        ? { excludedContentCategories: [...(excludedContentCategoriesWire ?? [])] }
        : excludedEvidenceCategoryScopesUi != null
          ? {
              excludedContentCategories: fileTypesTokensForEvidenceListing(
                excludedEvidenceCategoryScopesUi,
              ),
            }
          : {}),
      ...(item.excluded_evidence_category_scopes != null
        ? {
            excludedEvidenceCategoryScopes: excludedEvidenceCategoryScopesUi ?? [],
          }
        : excludedContentCategoriesWire != null
          ? {
              excludedEvidenceCategoryScopes: uiKeysForEvidenceListingTokens(
                excludedContentCategoriesWire,
              ),
            }
          : {}),
    } as ScopesObject,
  };
}
